import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Loader2, Check, ChevronDown, ChevronRight, Megaphone } from 'lucide-react';
import { useProject } from '../../../contexts/ProjectContext';
import { contentApi, type BrandVoice } from '../../../api/content';
import { SettingsPageLayout } from '../../../components/layout';

const BrandVoicePage = () => {
  const navigate = useNavigate();
  const { currentProject, buildProjectUrl } = useProject();
  const workspaceId = currentProject?.workspaceId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved'>('idle');

  // Form state
  const [guidelines, setGuidelines] = useState('');
  const [toneKeywords, setToneKeywords] = useState('');
  const [avoidKeywords, setAvoidKeywords] = useState('');
  const [examples, setExamples] = useState('');
  const [channelRules, setChannelRules] = useState<Record<string, string>>({
    linkedin: '',
    twitter: '',
    newsletter: '',
  });
  const [expandedChannels, setExpandedChannels] = useState<Record<string, boolean>>({});

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!workspaceId) { setLoading(false); return; }
    setLoading(true);
    contentApi.getBrandVoice(workspaceId)
      .then(bv => {
        if (bv) {
          setGuidelines(bv.guidelines || '');
          setToneKeywords((bv.tone_keywords || []).join(', '));
          setAvoidKeywords((bv.avoid_keywords || []).join(', '));
          setExamples(
            Array.isArray(bv.examples)
              ? bv.examples.map(e => typeof e === 'string' ? e : JSON.stringify(e)).join('\n\n---\n\n')
              : ''
          );
          const rules = (bv.channel_rules || {}) as Record<string, string>;
          setChannelRules({
            linkedin: rules.linkedin || '',
            twitter: rules.twitter || '',
            newsletter: rules.newsletter || '',
          });
        }
      })
      .catch(err => console.error('Failed to load brand voice', err))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const autoSave = useCallback(() => {
    if (!workspaceId) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const toneArr = toneKeywords.split(',').map(s => s.trim()).filter(Boolean);
        const avoidArr = avoidKeywords.split(',').map(s => s.trim()).filter(Boolean);
        const examplesArr = examples.split('\n\n---\n\n').filter(Boolean);

        const rules: Record<string, string> = {};
        if (channelRules.linkedin) rules.linkedin = channelRules.linkedin;
        if (channelRules.twitter) rules.twitter = channelRules.twitter;
        if (channelRules.newsletter) rules.newsletter = channelRules.newsletter;

        await contentApi.upsertBrandVoice(workspaceId, {
          guidelines: guidelines || null,
          tone_keywords: toneArr,
          avoid_keywords: avoidArr,
          examples: examplesArr,
          channel_rules: rules,
        } as Partial<BrandVoice>);

        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (err) {
        console.error('Failed to save brand voice', err);
      } finally {
        setSaving(false);
      }
    }, 1500);
  }, [workspaceId, guidelines, toneKeywords, avoidKeywords, examples, channelRules]);

  const toggleChannel = (ch: string) => {
    setExpandedChannels(prev => ({ ...prev, [ch]: !prev[ch] }));
  };

  if (loading) {
    return (
      <SettingsPageLayout
        title="Brand Voice"
        subtitle="Company-level brand guidelines that shape all AI-generated social copy"
        icon={<Megaphone size={20} />}
        backTo={buildProjectUrl('da', 'sm')}
        backLabel="Dashboard"
      >
        <div className="space-y-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-neutral-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </SettingsPageLayout>
    );
  }

  return (
    <SettingsPageLayout
      title="Brand Voice"
      subtitle="Company-level brand guidelines that shape all AI-generated social copy. Changes auto-save."
      icon={<Megaphone size={20} />}
      backTo={buildProjectUrl('da', 'sm')}
      backLabel="Dashboard"
      actions={
        <div className="flex items-center gap-2 text-xs text-neutral-400">
          {saving && (
            <span className="flex items-center gap-1 text-amber-600">
              <Loader2 size={12} className="animate-spin" /> Saving...
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="flex items-center gap-1 text-emerald-600">
              <Check size={12} /> Saved
            </span>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        {/* Brand Personality & Guidelines */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
          <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 block">
            Brand Personality & Guidelines
          </label>
          <p className="text-[11px] text-neutral-400 mb-3">
            Paste the full brand personality document. This sets the outer guardrails for all generated content.
          </p>
          <textarea
            value={guidelines}
            onChange={e => { setGuidelines(e.target.value); autoSave(); }}
            onBlur={autoSave}
            rows={12}
            className="w-full text-sm bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2
              focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300 resize-y"
            placeholder="Describe your brand personality, tone of voice, values, and communication style..."
          />
        </div>

        {/* Tone Keywords */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
          <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 block">
            Tone Keywords
          </label>
          <p className="text-[11px] text-neutral-400 mb-3">
            Comma-separated keywords that describe your brand tone.
          </p>
          <input
            type="text"
            value={toneKeywords}
            onChange={e => { setToneKeywords(e.target.value); autoSave(); }}
            onBlur={autoSave}
            className="w-full text-sm bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2
              focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300"
            placeholder="approachable, direct, honest, no-fluff, gets things done"
          />
        </div>

        {/* Words to Avoid */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
          <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 block">
            Words to Avoid
          </label>
          <p className="text-[11px] text-neutral-400 mb-3">
            Comma-separated words or phrases that should never appear in generated content.
          </p>
          <input
            type="text"
            value={avoidKeywords}
            onChange={e => { setAvoidKeywords(e.target.value); autoSave(); }}
            onBlur={autoSave}
            className="w-full text-sm bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2
              focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300"
            placeholder="synergy, leverage, thought leader, disrupt, game-changer"
          />
        </div>

        {/* Brand Examples */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
          <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 block">
            Brand Examples
          </label>
          <p className="text-[11px] text-neutral-400 mb-3">
            Example posts that embody the brand voice. Separate multiple examples with --- on its own line.
          </p>
          <textarea
            value={examples}
            onChange={e => { setExamples(e.target.value); autoSave(); }}
            onBlur={autoSave}
            rows={8}
            className="w-full text-xs font-mono bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2
              focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300 resize-y"
            placeholder={"Example brand-aligned post here...\n\n---\n\nAnother example post..."}
          />
        </div>

        {/* Channel Rules */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
          <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3 block">
            Channel Rules
          </label>
          <p className="text-[11px] text-neutral-400 mb-4">
            Per-platform overrides for length, hashtag policy, emoji usage, and tone.
          </p>

          {[
            { id: 'linkedin', label: 'LinkedIn' },
            { id: 'twitter', label: 'Twitter/X' },
            { id: 'newsletter', label: 'Newsletter' },
          ].map(ch => (
            <div key={ch.id} className="border-t border-neutral-100 first:border-t-0">
              <button
                onClick={() => toggleChannel(ch.id)}
                className="flex items-center gap-2 w-full py-3 text-sm font-medium text-neutral-700 hover:text-neutral-900"
              >
                {expandedChannels[ch.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {ch.label}
                {channelRules[ch.id] && (
                  <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                    configured
                  </span>
                )}
              </button>
              {expandedChannels[ch.id] && (
                <textarea
                  value={channelRules[ch.id]}
                  onChange={e => {
                    setChannelRules(prev => ({ ...prev, [ch.id]: e.target.value }));
                    autoSave();
                  }}
                  onBlur={autoSave}
                  rows={4}
                  className="w-full text-xs bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 mb-3
                    focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300 resize-y"
                  placeholder={`Rules for ${ch.label}: max length, hashtag policy, emoji usage, tone overrides...`}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </SettingsPageLayout>
  );
};

export default BrandVoicePage;
